import React, { Component } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import currencyFormatter from "currency-formatter";
import FormattedMessage from "grommet/components/FormattedMessage";
import List from "grommet/components/List";
import ListItem from "grommet/components/ListItem";
import Notification from "grommet/components/Notification";
import CSSClassnames from "grommet/utils/CSSClassnames";
import Announcer from "grommet/utils/Announcer";
import { translate } from "react-i18next";
import AppSettings from "../../utils/appSettings";

const NO_PADDING = "NO_PADDING";
const PAD_SERIES_MORE_THAN_TOTAL = "PAD_SERIES_MORE_THAN_TOTAL";
const PAD_EVERYTHING = "PAD_EVERYTHING";
const PAD_AMOUNT = 150;

const CLASS_ROOT = CSSClassnames.LEGEND;
const COLOR_INDEX = CSSClassnames.COLOR_INDEX;

const getTotalPaddingFromPadSetting = padding => {
    switch (padding) {
        case undefined:
            return "inherit";
        case NO_PADDING:
            return "inherit";
        case PAD_EVERYTHING:
            return `${PAD_AMOUNT}px`;
        case PAD_SERIES_MORE_THAN_TOTAL:
            return `${PAD_AMOUNT}px`;
        default:
            throw new Error("Unknown padding setting", padding);
    }
};

const getSeriesPaddingFromPadSetting = padding => {
    switch (padding) {
        case undefined:
            return "inherit";
        case NO_PADDING:
            return "inherit";
        case PAD_EVERYTHING:
            return `${PAD_AMOUNT}px`;
        case PAD_SERIES_MORE_THAN_TOTAL:
            return `${PAD_AMOUNT * 1.7}px`;
        default:
            throw new Error("Unknown padding setting", padding);
    }
};

function getMaxDecimalDigits(series) {
    let maxDigits = 0;
    series.forEach(item => {
        const currentDigitsGroup = /\.(\d*)$/.exec(item.value.toString());
        if (currentDigitsGroup) {
            const currentDigits = currentDigitsGroup[1].length;
            maxDigits = Math.max(maxDigits, currentDigits);
        }
    });

    return Math.pow(10, maxDigits);
}

class Legend extends Component {
    constructor(props, context) {
        super(props, context);

        this.t = props.t;
        this._onActive = this._onActive.bind(this);
        this._renderSeries = this._renderSeries.bind(this);
        this._renderSwatch = this._renderSwatch.bind(this);
        this._renderLabel = this._renderLabel.bind(this);
        this._renderValue = this._renderValue.bind(this);
        this._renderTotal = this._renderTotal.bind(this);
        this._seriesTotal = this._seriesTotal.bind(this);

        this.state = { activeIndex: this.props.activeIndex, error: undefined };
    }

    componentDidCatch(error) {
        this.setState({
            error
        });
    }

    componentWillReceiveProps(newProps) {
        if (newProps.activeIndex !== this.state.activeIndex) {
            this.setState({ activeIndex: newProps.activeIndex });
        }
    }

    componentDidUpdate() {
        const { announce } = this.props;
        if (announce) {
            Announcer.announce(this.legendRef.textContent);
        }
    }

    _onActive(index) {
        const { onActive } = this.props;
        this.setState({ activeIndex: index });
        if (onActive) {
            onActive(index);
        }
    }

    _itemColorIndex(item, index) {
        return item.colorIndex || `graph-${index + 1}`;
    }

    _renderSwatch(item, index) {
        if (!item.dualColor) {
            const colorIndex = this._itemColorIndex(item, index);
            return (
                <svg
                    style={{ overflow: "visible" }}
                    className={`${CLASS_ROOT}__item-swatch ${COLOR_INDEX}-${colorIndex}`}
                    viewBox="0 0 12 12"
                >
                    <path className={item.className} d="M 5 0 l 0 12" />
                </svg>
            );
        } else {
            return (
                <svg style={{ overflow: "visible" }} className={`${CLASS_ROOT}__item-swatch`} viewBox="0 0 12 12">
                    <path
                        className={`${item.className}`}
                        style={{
                            stroke: "#DC2878"
                        }}
                        d="M 5 0 l 0 6"
                    />
                    <path
                        className={`${item.className}`}
                        style={{
                            stroke: "#0A64A0"
                        }}
                        d="M 5 6 l 0 6"
                    />
                </svg>
            );
        }
    }

    _renderLabel(item, swatch) {
        if (swatch) {
            return (
                <span className={`${CLASS_ROOT}__item-label`} style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                    {swatch}
                    {item.label}
                </span>
            );
        } else {
            return <span className={`${CLASS_ROOT}__item-label`}>{item.label}</span>;
        }
    }

    _renderValue(item) {
        const { units } = this.props;
        const unitsValue = item.units || units;
        const valueClasses = `${CLASS_ROOT}__item-value`;
        let unitsPrefix;
        let unitsSuffix;
        if (unitsValue) {
            if (unitsValue.prefix) {
                unitsPrefix = <span className={`${CLASS_ROOT}__item-units`}>{unitsValue.prefix}</span>;
            }
            if (unitsValue.suffix || (typeof unitsValue === "string" || unitsValue instanceof String)) {
                unitsSuffix = <span className={`${CLASS_ROOT}__item-units`}>{unitsValue.suffix || unitsValue}</span>;
            }
        }
        return (
            <span className={valueClasses} style={{ display: "flex", flexDirection: "row" }}>
                {unitsPrefix}
                {currencyFormatter.format(item.value, AppSettings.currencyOptionFormater)}
                {unitsSuffix}
            </span>
        );
    }

    _seriesTotal() {
        const { series } = this.props;
        const maxDecimalDigits = getMaxDecimalDigits(series);
        let total = 0;
        series.forEach(item => (total += (typeof item.value === "number" ? item.value : 0) * maxDecimalDigits));
        return total / maxDecimalDigits;
    }

    _renderSeries() {
        const { series, padding } = this.props;
        const { activeIndex } = this.state;

        return series.map((item, index) => {
            const legendClasses = classnames(`${CLASS_ROOT}__item`, {
                [`${CLASS_ROOT}__item--active`]: index === activeIndex,
                [`${CLASS_ROOT}__item--clickable`]: item.onClick
            });

            let swatch;
            if (item.hasOwnProperty("colorIndex")) {
                swatch = this._renderSwatch(item, index);
            }

            let label;
            if (item.hasOwnProperty("label")) {
                label = this._renderLabel(item, swatch);
            }

            let value;
            if (item.hasOwnProperty("value")) {
                value = this._renderValue(item);
            }

            return (
                <ListItem
                    onClick={item.onClick}
                    justify="between"
                    separator="none"
                    style={{
                        paddingRight: getSeriesPaddingFromPadSetting(padding)
                    }}
                    pad={{ horizontal: "small" }}
                    key={`${item.label}-${index}` || index}
                    className={legendClasses}
                    onMouseOver={this._onActive.bind(this, index)}
                    onMouseOut={this._onActive.bind(this, undefined)}
                >
                    {label}
                    {value}
                </ListItem>
            );
        }, this);
    }

    _renderTotal() {
        const { total, units, padding } = this.props;
        let totalValue;
        if (total !== true) {
            totalValue = total;
        } else {
            totalValue = this._seriesTotal();
        }
        let unitsPrefix;
        let unitsSuffix;
        if (units && units.prefix) {
            unitsPrefix = <span className={`${CLASS_ROOT}__total-units`}>{units.prefix}</span>;
        }
        if (units && (units.suffix || (typeof units === "string" || units instanceof String))) {
            unitsSuffix = <span className={`${CLASS_ROOT}__total-units`}>{units.suffix || units}</span>;
        }

        return (
            <ListItem
                className={`${CLASS_ROOT}__total`}
                justify="between"
                separator="none"
                style={{
                    paddingRight: getTotalPaddingFromPadSetting(padding)
                }}
                pad={{ horizontal: "small" }}
                responsive={false}
            >
                <span className={`${CLASS_ROOT}__total-label`}>
                    <FormattedMessage id="Total" defaultMessage={this.t(this.props.labelTotal || "Total")} />
                </span>
                <span className={`${CLASS_ROOT}__total-value`}>
                    {currencyFormatter.format(totalValue, AppSettings.currencyOptionFormater)}
                    {unitsSuffix}
                </span>
            </ListItem>
        );
    }

    render() {
        const { className, series, total, ...props } = this.props;
        const { error } = this.state;
        delete props.activeIndex;
        delete props.announce;
        delete props.onActive;
        delete props.units;

        const classes = classnames(CLASS_ROOT, className);
        let items = this._renderSeries();

        // build legend from bottom to top, to align with Meter bar stacking
        items.reverse();

        let totalNode;
        if (total && series.length > 1) {
            totalNode = this._renderTotal();
        }

        if (error) {
          return (
            <Notification
                        state={`Une erreur s'est produit lors de l'affichage du résultat`}
                        message="Erreur"
                        status="critical"
                        size="large"
                    />
          )
        }
        return (
            <List ref={ref => (this.legendRef = ref)} {...props} className={classes}>
                {items.reverse()}
                {totalNode}
            </List>
        );
    }
}

export default translate()(Legend);

Legend.defaultProps = {
    announce: false
};

Legend.propTypes = {
    activeIndex: PropTypes.number,
    announce: PropTypes.bool,
    onActive: PropTypes.func,
    series: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.oneOfType([PropTypes.number, PropTypes.node]),
            units: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({
                    prefix: PropTypes.string,
                    suffix: PropTypes.string
                })
            ]),
            colorIndex: PropTypes.oneOfType([
                PropTypes.number, // 1-6
                PropTypes.string // status
            ]),
            onClick: PropTypes.func
        })
    ).isRequired,
    total: PropTypes.oneOfType([PropTypes.bool, PropTypes.node]),
    labelTotal: PropTypes.string,
    padding: PropTypes.oneOf([NO_PADDING, PAD_EVERYTHING, PAD_SERIES_MORE_THAN_TOTAL]),
    units: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            prefix: PropTypes.string,
            suffix: PropTypes.string
        })
    ])
};



// WEBPACK FOOTER //
// ./src/computationApps/components/viewer/legend.js